import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import type { MeshBuilder } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { horizontalCap, octahedronSolid, planPoint, pyramidOverRing, ringPoints } from '../solids';
import { buildVessel, vesselOutline, VESSEL } from './churchVessel';
import type { VesselPlan } from './churchModel';
import { centredWindow, rectWall } from './masonry';
import { meshFrameOf, planFrameOf, toLayout } from './planFrame';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';

/** The 1544 woodcut shows the medieval nave/choir and one northern vessel.
 * Its original OSM centroid remains the component anchor when the reservation
 * expands to include the convent; the later 1661 south vessel stays absent. */
export function paulusbroedersPlan(landmark: Landmark) {
  const x = landmark.params['anchorX'],
    y = landmark.params['anchorY'];
  const plan =
    typeof x === 'number' && typeof y === 'number'
      ? { origin: [x, y] as Vec2, heading: landmark.heading, ring: [] }
      : planFrameOf(landmark.footprint, landmark.heading);
  const main: VesselPlan = {
    u0: -18.6,
    chordU: 17.9,
    vc: -0.3,
    half: 3.8,
    eaveY: 12,
    ridgeY: 16.6,
    apse: 'polygonal',
  };
  const north: VesselPlan = {
    u0: -18.6,
    chordU: 15.1,
    vc: 7.1,
    half: 3.6,
    eaveY: 10.8,
    ridgeY: 14.9,
    apse: 'flat',
  };
  return {
    plan,
    frame: meshFrameOf(plan),
    main,
    north,
    belfry: {
      centre: [-10.8, main.vc] as Vec2,
      footY: 16.2,
      eaveY: 18.1,
      topY: 20.4,
      radius: 0.68,
    },
  };
}

export function buildPaulusbroederskapel(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors,
  ids: { readonly edgeId: number; readonly seed: number },
  tracery: TracerySink = NO_TRACERY
): number {
  const before = builder.triangleCount,
    base = paintedChapelStyles(colors, ids, tracery),
    styles = { ...base, wall: { ...base.wall, surface: SURFACE.brick } };
  const { frame, main, north, belfry } = paulusbroedersPlan(landmark);
  for (const vessel of [main, north])
    buildVessel(builder, styles, frame, vessel, {
      longWindows: { sill: 2.6, top: vessel.eaveY - 1.2 },
      westWindow: true,
      bay: 5.0,
    });
  const ring = ringPoints(belfry.centre, belfry.radius, 8, Math.PI / 8);
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % ring.length]!,
      width = Math.hypot(b[0] - a[0], b[1] - a[1]);
    rectWall(
      builder,
      styles,
      frame,
      [a, b],
      [belfry.footY, belfry.eaveY],
      centredWindow(width, 0.4, 1.5, 0.48),
      belfry.footY
    );
  });
  horizontalCap(builder, styles.wall, frame, ring, belfry.footY, false);
  pyramidOverRing(builder, styles, frame, ring, belfry.eaveY, {
    plan: belfry.centre,
    y: belfry.topY,
  });
  octahedronSolid(
    builder,
    styles.wall,
    frame,
    planPoint(belfry.centre[0], belfry.topY + 0.12, belfry.centre[1]),
    0.16
  );
  return builder.triangleCount - before;
}

/** Collision follows the two medieval vessels and their apse buttresses. It does not
 * turn the reserved but unbuilt 1661 south vessel into an invisible solid obstruction. */
export function paulusbroedersObstacles(landmark: Landmark): readonly Ring[] {
  const { plan, main, north } = paulusbroedersPlan(landmark),
    outline = vesselOutline(main);
  const buttresses = outline.slice(1, -1).map((at): Ring => {
    const d: Vec2 = [at[0] - main.chordU, at[1] - main.vc],
      length = Math.hypot(...d);
    const outward: Vec2 = [d[0] / length, d[1] / length],
      along: Vec2 = [-outward[1], outward[0]];
    const corner = (s: number, depth: number): Vec2 => [
      at[0] + (along[0] * s * VESSEL.buttress.width) / 2 + outward[0] * depth,
      at[1] + (along[1] * s * VESSEL.buttress.width) / 2 + outward[1] * depth,
    ];
    return [
      corner(-1, -0.1),
      corner(1, -0.1),
      corner(1, VESSEL.buttress.depth),
      corner(-1, VESSEL.buttress.depth),
    ];
  });
  return [outline, vesselOutline(north), ...buttresses].map((ring) =>
    ring.map((point) => toLayout(plan, point))
  );
}
